import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Trophy, Star, MessageCircle, Plus } from 'lucide-react';
import { API } from '../config/constants';
import BottomNav from '../components/BottomNav';

const getRankStyle = (rank) => {
  if (rank === 1) return 'bg-gradient-to-r from-yellow-400 to-yellow-600 text-black';
  if (rank === 2) return 'bg-gradient-to-r from-gray-300 to-gray-400 text-black';
  if (rank === 3) return 'bg-gradient-to-r from-amber-600 to-amber-800 text-white';
  return 'bg-white/10 text-white';
};

const getRankEmoji = (rank) => {
  if (rank === 1) return '🥇';
  if (rank === 2) return '🥈';
  if (rank === 3) return '🥉';
  return `#${rank}`;
};

const LeaderboardItem = ({ item, rank, type, onNavigate }) => (
  <div 
    onClick={() => onNavigate(`/user/${item.user?.id}`)}
    className="flex items-center gap-3 p-3 bg-white/5 rounded-xl cursor-pointer hover:bg-white/10 transition-colors"
  >
    {/* Rank */}
    <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm ${getRankStyle(rank)}`}>
      {rank <= 3 ? getRankEmoji(rank) : rank}
    </div>
    
    {/* Avatar */}
    {item.user?.profile_pic ? (
      <img src={item.user.profile_pic} alt={item.user?.username} className="w-12 h-12 rounded-full object-cover" />
    ) : (
      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-amber-500 to-amber-700 flex items-center justify-center text-white font-bold">
        {item.user?.username?.charAt(0).toUpperCase() || 'U'}
      </div>
    )}
    
    {/* Info */}
    <div className="flex-1">
      <p className="text-white font-semibold">{item.user?.username || 'Unknown'}</p>
      <p className="text-white/50 text-sm">
        {type === 'raters' && `${item.count} ${item.count === 1 ? 'rating' : 'ratings'}`}
        {type === 'commenters' && `${item.count} ${item.count === 1 ? 'comment' : 'comments'}`}
        {type === 'discoverers' && `${item.count} ${item.count === 1 ? 'cigar' : 'cigars'} added`}
      </p>
    </div>
    
    {/* Stat Badge */}
    <div className="bg-amber-500/20 px-3 py-1 rounded-full">
      <span className="text-amber-500 font-bold">
        {item.count}
      </span>
    </div>
  </div>
);

const tabs = [
  { id: 'raters', label: 'Top Raters', icon: Star },
  { id: 'commenters', label: 'Most Active', icon: MessageCircle },
  { id: 'discoverers', label: 'Discoverers', icon: Plus }
];

const LeaderboardsPage = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('raters');
  const [topRaters, setTopRaters] = useState([]);
  const [topCommenters, setTopCommenters] = useState([]);
  const [cigarDiscoverers, setCigarDiscoverers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchLeaderboards();
  }, []);

  const fetchLeaderboards = async () => {
    try {
      const [ratersRes, commentersRes, discoverersRes] = await Promise.all([
        axios.get(`${API}/leaderboards/top-raters`),
        axios.get(`${API}/leaderboards/top-commenters`),
        axios.get(`${API}/leaderboards/cigar-discoverers`)
      ]);
      setTopRaters(ratersRes.data || []);
      setTopCommenters(commentersRes.data || []);
      setCigarDiscoverers(discoverersRes.data || []);
    } catch (error) {
      console.error('Failed to fetch leaderboards');
    } finally {
      setLoading(false);
    }
  };

  const getCurrentData = () => {
    switch (activeTab) {
      case 'raters': return topRaters;
      case 'commenters': return topCommenters;
      case 'discoverers': return cigarDiscoverers;
      default: return [];
    }
  };

  return (
    <div className="min-h-screen bg-[#0B0B0C] flex flex-col pb-24">
      {/* Header */}
      <div className="p-4 border-b border-white/10">
        <h1 className="playfair text-2xl text-white text-center flex items-center justify-center gap-2">
          <Trophy className="text-amber-500" size={28} />
          Leaderboards
        </h1>
      </div>

      {/* Tabs */}
      <div className="flex p-2 gap-2 bg-white/5">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-lg transition-colors ${
              activeTab === tab.id 
                ? 'bg-amber-600 text-white' 
                : 'text-white/50 hover:text-white'
            }`}
          >
            <tab.icon size={18} />
            <span className="text-sm font-medium">{tab.label}</span>
          </button>
        ))}
      </div>

      {/* Leaderboard List */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {loading ? (
          <div className="text-center text-white/50 py-8">Loading...</div>
        ) : getCurrentData().length === 0 ? (
          <div className="text-center text-white/50 py-8">
            No data yet. Be the first to {activeTab === 'raters' ? 'rate cigars' : activeTab === 'commenters' ? 'start discussions' : 'add cigars'}!
          </div>
        ) : (
          getCurrentData().map((item, index) => (
            <LeaderboardItem 
              key={item.user?.id || index} 
              item={item} 
              rank={index + 1}
              type={activeTab} 
              onNavigate={navigate} 
            />
          ))
        )}
      </div>

      <BottomNav />
    </div>
  );
};

export default LeaderboardsPage;
